import { Link } from "@tanstack/react-router";
import clsx from "clsx";
import {
  ChevronDown,
  ClipboardPaste,
  Command,
  Copy,
  Keyboard as KeyboardIcon,
  Loader2,
  Maximize,
  Minimize,
  Minus,
  Monitor,
  MonitorPlay,
  Ratio,
  RotateCw,
  Settings2,
  Shrink,
  SquareTerminal,
  TextCursorInput,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { useTranslation } from "react-i18next";
import type { SessionKind, SessionState } from "../terminal/store.js";
import { OsIcon } from "./os-icon.js";
import { Dialog, Menu, type MenuItem } from "./overlay.js";
import { Button } from "./primitives.js";

/*
 * The session window every kind shares — SSH terminals, remote desktops and
 * the server console (docs/terminal.md, "Session window"): one toolbar with
 * its three parts, one button, one status dot, one state card, the kind
 * icons. Controls use the panel's tokens and follow light and dark; the
 * session screen is dark (`bg-screen-bg`), a terminal draws in its own
 * colour theme.
 */

/** The kind of a session, as an icon: terminal, remote desktop, console. */
export const KIND_ICON: Record<SessionKind, LucideIcon> = {
  ssh: SquareTerminal,
  rdp: Monitor,
  vnc: MonitorPlay,
};

export function KindIcon({ kind, className }: { kind: SessionKind; className?: string }) {
  const Icon = KIND_ICON[kind];
  return <Icon className={clsx("shrink-0", className ?? "size-4")} aria-hidden />;
}

/** The one status scheme: tabs, the phone sheet, the toolbars. */
export const STATE_DOT: Record<SessionState, string> = {
  connecting: "bg-warn animate-pulse",
  connected: "bg-ok",
  disconnected: "bg-muted",
  error: "bg-danger",
};

export function StatusDot({ state, className }: { state: SessionState; className?: string }) {
  return (
    <span
      className={clsx("shrink-0 rounded-full", STATE_DOT[state], className ?? "size-2")}
      aria-hidden
    />
  );
}

/** One control of a session toolbar: always an icon, its label as tooltip and name. */
export interface SessionTool {
  /** Stable id (`data-tool`, React key). */
  id: string;
  label: string;
  icon: LucideIcon;
  onSelect?: () => void;
  /** A switch (keyboard, full screen…): `aria-pressed`. */
  pressed?: boolean;
  disabled?: boolean;
  /** A small menu instead of an action («Сочетания клавиш ▾», «Питание ▾»). */
  items?: readonly MenuItem[];
}

const BUTTON =
  "inline-flex min-h-9 min-w-9 shrink-0 items-center justify-center gap-1 rounded-md px-2 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50";

/** The one toolbar button of sessions: icon only, the label as tooltip and accessible name. */
export function SessionToolButton({ tool }: { tool: SessionTool }) {
  const Icon = tool.icon;
  const look = clsx(
    BUTTON,
    tool.pressed
      ? "bg-accent-soft text-accent-strong"
      : "text-muted enabled:hover:bg-surface-2 enabled:hover:text-text",
  );
  const inside = (
    <>
      <Icon className="size-4" aria-hidden />
      {tool.items && <ChevronDown className="size-3" aria-hidden />}
    </>
  );
  if (tool.items) {
    return (
      <Menu
        align="start"
        trigger={
          <button
            type="button"
            data-tool={tool.id}
            aria-label={tool.label}
            title={tool.label}
            disabled={tool.disabled}
            className={look}
          >
            {inside}
          </button>
        }
        groups={[[...tool.items]]}
      />
    );
  }
  return (
    <button
      type="button"
      data-tool={tool.id}
      onClick={tool.onSelect}
      disabled={tool.disabled}
      aria-label={tool.label}
      aria-pressed={tool.pressed}
      title={tool.label}
      className={look}
    >
      {inside}
    </button>
  );
}

function Zone({
  tools,
  label,
  className,
}: {
  tools: readonly SessionTool[];
  label: string;
  className?: string;
}) {
  if (tools.length === 0) return null;
  return (
    <span role="group" aria-label={label} className={clsx("flex items-center gap-0.5", className)}>
      {tools.map((tool) => (
        <SessionToolButton key={tool.id} tool={tool} />
      ))}
    </span>
  );
}

/** Which ends of a horizontal scroller have more content past them. */
export interface ScrollEdges {
  start: boolean;
  end: boolean;
}

/** Less than a pixel of overflow is rounding, not content (zoomed pages give fractions). */
const EDGE_SLACK_PX = 1;

/** From a scroller's metrics: is there more to the left, more to the right? */
export const scrollEdges = (box: {
  scrollLeft: number;
  scrollWidth: number;
  clientWidth: number;
}): ScrollEdges => ({
  start: box.scrollLeft > EDGE_SLACK_PX,
  end: box.scrollLeft + box.clientWidth < box.scrollWidth - EDGE_SLACK_PX,
});

/**
 * A row that scrolls sideways when its content does not fit, without a
 * visible scrollbar, and fades out at each end that has more content past
 * it: the right at the start, both in the middle, the left at the end. The
 * content stays in the page and in the tab order as it is; the browser
 * scrolls a focused control into view.
 */
export function FadingScroller({
  children,
  className,
  innerClassName,
}: {
  children: ReactNode;
  className?: string;
  innerClassName?: string;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState<ScrollEdges>({ start: false, end: false });
  useEffect(() => {
    const element = scroller.current;
    if (!element) return;
    const sync = () =>
      setEdges((previous) => {
        const next = scrollEdges(element);
        return next.start === previous.start && next.end === previous.end ? previous : next;
      });
    sync();
    element.addEventListener("scroll", sync, { passive: true });
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(sync) : null;
    observer?.observe(element);
    if (element.firstElementChild) observer?.observe(element.firstElementChild);
    return () => {
      element.removeEventListener("scroll", sync);
      observer?.disconnect();
    };
  }, []);
  // From the bar's colour to nothing: the tools under it fade out, none is hidden by a block.
  const fade = "pointer-events-none absolute inset-y-0 z-10 w-8 from-surface to-transparent";
  return (
    <div
      className={clsx("relative min-w-0", className)}
      data-scroll-start={edges.start || undefined}
      data-scroll-end={edges.end || undefined}
    >
      <div
        ref={scroller}
        data-scroller
        className="flex min-w-0 overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <div className={clsx("flex w-max min-w-full items-center", innerClassName)}>{children}</div>
      </div>
      {edges.start && (
        <span aria-hidden data-fade="start" className={clsx(fade, "left-0 bg-linear-to-r")} />
      )}
      {edges.end && (
        <span aria-hidden data-fade="end" className={clsx(fade, "right-0 bg-linear-to-l")} />
      )}
    </div>
  );
}

/**
 * Asks before a reconnect ends a session that still works (owner's request
 * of 2026-10-22): what goes with it differs per kind. Never shown for a
 * session that is disconnected or failed: there is nothing to lose.
 */
export function ReconnectDialog({
  open,
  onOpenChange,
  kind,
  serverName,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kind: SessionKind;
  serverName: string;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("sessions.reconnect.title", { name: serverName })}
      description={t(`sessions.reconnect.${kind}`)}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            icon={<RotateCw className="size-4" aria-hidden />}
            onClick={() => {
              onOpenChange(false);
              onConfirm();
            }}
          >
            {t("sessions.tools.reconnect")}
          </Button>
        </>
      }
    />
  );
}

/** What the reconnect question needs to know of a session. */
export interface ReconnectTarget {
  kind: SessionKind;
  serverName: string;
  state: SessionState;
}

/**
 * «Переподключиться» of a session: at once when it is not connected, after
 * `ReconnectDialog` when it is. Returns the request and the dialog to render.
 */
export function useReconnectConfirm(target: ReconnectTarget | null, reconnect: () => void) {
  const [asking, setAsking] = useState(false);
  const request = () => {
    if (target?.state === "connected") setAsking(true);
    else reconnect();
  };
  const dialog = target ? (
    <ReconnectDialog
      open={asking}
      onOpenChange={setAsking}
      kind={target.kind}
      serverName={target.serverName}
      onConfirm={reconnect}
    />
  ) : null;
  return { request, dialog };
}

/**
 * The toolbar of every session window, in three parts (owner's spec of
 * 2026-10-21, changed on 2026-10-22): left — who and the status, then
 * «Переподключиться», «Настройки сеанса» and a kind's own `left` tools (a
 * console's «Питание ▾»); centre — the session's tools as
 * icons, in the order `centreTools` gives; right — the view of the window
 * (`rightTools`). The name opens the server's page. Every tool is on the bar
 * at every width: what does not fit scrolls sideways (`FadingScroller`).
 */
export function SessionToolbar({
  kind,
  serverName,
  who,
  osFlavor,
  osVersion,
  state,
  stateText,
  projectId,
  serverId,
  onReconnect,
  onSettings,
  left = [],
  centre,
  right,
}: {
  kind: SessionKind;
  serverName: string;
  /** The user, or «VNC» for a console. */
  who: string;
  osFlavor: string | null;
  osVersion: string | null;
  state: SessionState;
  stateText: string;
  projectId: string;
  serverId: string;
  /** Reconnects; a connected session asks first (`ReconnectDialog`). */
  onReconnect: () => void;
  /** «Настройки сеанса». */
  onSettings: () => void;
  /** More of the session's own controls after the settings (a console's «Питание ▾»). */
  left?: readonly SessionTool[];
  centre: readonly SessionTool[];
  right: readonly SessionTool[];
}) {
  const { t } = useTranslation();
  const reconnect = useReconnectConfirm({ kind, serverName, state }, onReconnect);
  return (
    <div
      role="toolbar"
      aria-label={t("sessions.toolbar")}
      data-kind={kind}
      className="flex min-h-11 items-center gap-1 border-b border-line bg-surface px-2 py-1 text-text"
    >
      {/* At most a little under half a phone's width: the tools keep room to scroll in. */}
      <span className="flex min-w-0 max-w-[45%] shrink items-center gap-2 px-1 text-sm sm:max-w-none">
        <OsIcon flavor={osFlavor} version={osVersion} variant="brand" />
        <Link
          to="/projects/$projectId/servers/$serverId"
          params={{ projectId, serverId }}
          title={t("sessions.toServer", { name: serverName })}
          // 24 px tall at least (WCAG 2.5.8): on a phone the scrolled tools pass close by it.
          className="block min-h-6 truncate py-0.5 font-medium leading-5 underline-offset-2 hover:underline"
        >
          {serverName}
        </Link>
        <span className="hidden truncate text-xs text-muted sm:inline">{who}</span>
        <span
          className="flex items-center gap-1.5 whitespace-nowrap text-xs text-muted"
          role="status"
          data-state={state}
        >
          <StatusDot state={state} />
          <span className="sr-only md:not-sr-only">{stateText}</span>
        </span>
      </span>
      <FadingScroller className="flex-1" innerClassName="gap-1">
        <span
          role="group"
          aria-label={t("sessions.zones.session")}
          className="flex items-center gap-0.5"
        >
          <SessionToolButton
            tool={{
              id: "reconnect",
              label: t("sessions.tools.reconnect"),
              icon: RotateCw,
              onSelect: reconnect.request,
            }}
          />
          <SessionToolButton
            tool={{
              id: "settings",
              label: t("sessions.tools.settings"),
              icon: Settings2,
              onSelect: onSettings,
            }}
          />
          {left.map((tool) => (
            <SessionToolButton key={tool.id} tool={tool} />
          ))}
        </span>
        <span className="mx-1 h-5 w-px shrink-0 bg-line" aria-hidden />
        <span className="flex flex-1 items-center justify-center">
          <Zone tools={centre} label={t("sessions.zones.tools")} />
        </span>
        <Zone tools={right} label={t("sessions.zones.window")} />
      </FadingScroller>
      {reconnect.dialog}
    </div>
  );
}

type Translate = (key: string) => string;
type Combo = { label: string; onSelect: () => void };

/**
 * The centre of the toolbar, always in this order (owner's spec of
 * 2026-10-21; «Настройки сеанса» moved to the left on 2026-10-22):
 * «Клавиатура» (console), «Сочетания клавиш» (console, desktop), «Набрать
 * текст» (console), «Вставить из буфера» (all), «Копировать» (terminal: the
 * selection; desktop: its clipboard). A tool a kind does not have is left out.
 */
export function centreTools(
  t: Translate,
  options: {
    keyboard?: { shown: boolean; toggle: () => void };
    combos?: readonly Combo[];
    typeText?: () => void;
    paste: () => void;
    copy?: { label: string; onSelect: () => void };
    /** Not connected: nothing to send to. */
    disabled?: boolean;
  },
): SessionTool[] {
  const disabled = options.disabled ?? false;
  const tools: SessionTool[] = [];
  if (options.keyboard)
    tools.push({
      id: "keyboard",
      label: t("sessions.tools.keyboard"),
      icon: KeyboardIcon,
      pressed: options.keyboard.shown,
      onSelect: options.keyboard.toggle,
    });
  if (options.combos)
    tools.push({
      id: "combos",
      label: t("sessions.tools.combos"),
      icon: Command,
      disabled,
      items: options.combos,
    });
  if (options.typeText)
    tools.push({
      id: "type-text",
      label: t("sessions.tools.typeText"),
      icon: TextCursorInput,
      disabled,
      onSelect: options.typeText,
    });
  tools.push({
    id: "paste",
    label: t("sessions.tools.paste"),
    icon: ClipboardPaste,
    disabled,
    onSelect: options.paste,
  });
  if (options.copy)
    tools.push({
      id: "copy",
      label: options.copy.label,
      icon: Copy,
      onSelect: options.copy.onSelect,
    });
  return tools;
}

/**
 * The right of the toolbar, always in this order: «Вписать / 1:1» (console
 * and desktop), full screen (where the browser has it), «Свернуть во
 * вкладку» (the window into its tab in the top bar, `terminal/minimise.ts`).
 */
export function rightTools(
  t: Translate,
  options: {
    scale?: { value: "fit" | "actual"; set: (scale: "fit" | "actual") => void };
    fullscreen: { fullscreen: boolean; toggle: () => void; available: boolean };
    minimise: () => void;
  },
): SessionTool[] {
  const { scale, fullscreen: full } = options;
  return [
    ...(scale
      ? [
          {
            id: "scale-fit",
            label: t("sessions.tools.fit"),
            icon: Shrink,
            pressed: scale.value === "fit",
            onSelect: () => scale.set("fit"),
          },
          {
            id: "scale-actual",
            label: t("sessions.tools.actual"),
            icon: Ratio,
            pressed: scale.value === "actual",
            onSelect: () => scale.set("actual"),
          },
        ]
      : []),
    ...(full.available
      ? [
          {
            id: "fullscreen",
            label: t(
              full.fullscreen ? "sessions.tools.exitFullscreen" : "sessions.tools.fullscreen",
            ),
            icon: full.fullscreen ? Minimize : Maximize,
            pressed: full.fullscreen,
            onSelect: full.toggle,
          },
        ]
      : []),
    // A window's «minimise» line (owner's choice of 2026-10-22): the window folds into its tab.
    {
      id: "minimise",
      label: t("sessions.tools.minimise"),
      icon: Minus,
      onSelect: options.minimise,
    },
  ];
}

/**
 * The one card over a session that is not on screen yet or any more,
 * centred, the same for every kind: connecting (spinner and words), or
 * disconnected/error (title, reason, what helps, actions). A form when it
 * signs in again (`onSubmit`).
 */
export function SessionStateCard({
  title,
  text,
  busy = false,
  children,
  actions,
  onSubmit,
}: {
  title: string;
  text?: string | undefined;
  /** Connecting: a spinner and the words, nothing to do. */
  busy?: boolean;
  children?: ReactNode;
  actions?: ReactNode;
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
}) {
  const card =
    "flex max-h-full w-full max-w-xl flex-col gap-3 overflow-y-auto rounded-xl border border-line bg-surface p-5 text-text shadow-lg";
  if (busy) {
    return (
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-4">
        <div
          role="status"
          className="flex items-center gap-3 rounded-xl border border-line bg-surface px-5 py-3 text-sm text-text shadow-lg"
        >
          <Loader2 className="size-4 animate-spin text-muted" aria-hidden />
          {title}
        </div>
      </div>
    );
  }
  const body = (
    <>
      <h2 className="text-base font-semibold">{title}</h2>
      {text && <p className="text-sm text-muted">{text}</p>}
      {children}
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </>
  );
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-screen-bg/80 p-4">
      {onSubmit ? (
        <form role="alert" onSubmit={onSubmit} className={card}>
          {body}
        </form>
      ) : (
        <div role="alert" className={card}>
          {body}
        </div>
      )}
    </div>
  );
}

/**
 * The server of a tab is gone (deleted, or the person lost its project),
 * typically noticed after a reload brought the tab back: nothing to
 * reconnect to, only the tab to close.
 */
export function ServerGoneCard({ name, onClose }: { name: string; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <SessionStateCard
      title={t("sessions.gone.title")}
      text={t("sessions.gone.text", { name })}
      actions={
        <Button variant="secondary" icon={<X className="size-4" aria-hidden />} onClick={onClose}>
          {t("sessions.gone.close")}
        </Button>
      }
    />
  );
}

/** Full screen of a session window: whether it is on, and the switch (absent on iPhones). */
export function useFullscreen(root: RefObject<HTMLElement | null>, after?: () => void) {
  const [fullscreen, setFullscreen] = useState(false);
  useEffect(() => {
    const sync = () => setFullscreen(document.fullscreenElement === root.current);
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, [root]);
  const toggle = useCallback(() => {
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    else void root.current?.requestFullscreen().catch(() => undefined);
    after?.();
  }, [root, after]);
  const available = typeof document !== "undefined" && document.fullscreenEnabled;
  return { fullscreen, toggle, available };
}

/** Touch screens start with the on-screen keyboard shown. */
export const coarsePointer = () =>
  typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches;

/** The page of a session window: the toolbar on top, the screen under it, full height. */
export const SESSION_WINDOW = "flex min-h-0 flex-1 flex-col bg-screen-bg";
